import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Category, CategoryGroup } from "@/types/category";
import { useCategories } from "@/hooks/useCategories";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import React from "react";

interface CategoriesTabProps {
  categoryGroups: CategoryGroup[];
  sortField?: "name" | "group" | "quoteCount";
  sortDirection?: "asc" | "desc";
}

export const CategoriesTab = ({ 
  categoryGroups,
  sortField = "name",
  sortDirection = "asc"
}: CategoriesTabProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryGroupId, setNewCategoryGroupId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);

  const { 
    categories, 
    isLoading, 
    addCategory, 
    updateCategory, 
    deleteCategory 
  } = useCategories();

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (newCategoryName.trim()) {
      addCategory.mutate({
        name: newCategoryName.trim(),
        categoryGroupId: newCategoryGroupId
      });
      setIsAddDialogOpen(false);
      setNewCategoryName("");
      setNewCategoryGroupId(null);
    }
  };

  const handleUpdate = (id: string) => {
    if (editingName.trim()) {
      updateCategory.mutate({
        id,
        name: editingName.trim(),
        categoryGroupId: editingGroupId
      });
      setEditingId(null);
      setEditingName("");
      setEditingGroupId(null);
    }
  };

  const handleDelete = async (id: string, quoteCount: number) => {
    if (quoteCount > 0) {
      const confirmed = window.confirm(
        `This category contains ${quoteCount} quotes. Are you sure you want to delete it?`
      );
      if (!confirmed) return;
    }
    deleteCategory.mutate(id);
  };

  const startEditing = (category: Category) => {
    setEditingId(category.id);
    setEditingName(category.name);
    setEditingGroupId(category.categoryGroupId || null);
  };

  const sortedCategories = React.useMemo(() => {
    if (!categories) return [];
    
    return [...categories].sort((a, b) => {
      if (sortField === "quoteCount") {
        return (sortDirection === "asc" ? 1 : -1) * ((a.quoteCount || 0) - (b.quoteCount || 0));
      }
      if (sortField === "name") {
        return sortDirection === "asc" 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (sortField === "group") {
        const groupA = categoryGroups.find(g => g.id === a.categoryGroupId)?.name || "zzz";
        const groupB = categoryGroups.find(g => g.id === b.categoryGroupId)?.name || "zzz";
        return sortDirection === "asc"
          ? groupA.localeCompare(groupB)
          : groupB.localeCompare(groupA);
      }
      return 0;
    });
  }, [categories, sortField, sortDirection, categoryGroups]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Categories</h2>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Category</DialogTitle>
              <DialogDescription>
                Create a new category and optionally assign it to a group.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <Input
                  placeholder="Category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Group (Optional)</label>
                <div>
                  <span id="group-description" className="sr-only">Select a category group</span>
                  <Select 
                    value={newCategoryGroupId || "none"} 
                    onValueChange={(value) => setNewCategoryGroupId(value === "none" ? null : value)}
                    aria-describedby="group-description"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Group</SelectItem>
                      {categoryGroups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!newCategoryName.trim() || addCategory.isPending}
                >
                  {addCategory.isPending ? "Adding..." : "Add Category"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-center p-4">Loading categories...</div>
      ) : sortedCategories?.length === 0 ? (
        <div className="text-center p-4 text-muted-foreground">
          No categories found.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sortedCategories?.map((category) => (
            <Card key={category.id} className="relative">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">
                  {editingId === category.id ? (
                    <div className="flex items-center space-x-2">
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="h-8"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleUpdate(category.id)}
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    category.name
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">
                    Group: {categoryGroups.find(g => g.id === category.categoryGroupId)?.name || "No Group"}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Quotes: {category.quoteCount || 0}
                  </div>
                  {editingId !== category.id && (
                    <div className="flex justify-end space-x-2 mt-4">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEditing(category)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(category.id, category.quoteCount || 0)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
